// The grocery list's saved state (slice 6a, docs/2026-10-04-groceries-design.md):
// the household's marks on a week's list, read against the list the plan
// produces now. Pure functions, tested in tests/grocery-state.test.mjs.
//
// The list itself is never stored: buildGroceryList derives it from the
// plan. A mark remembers the line's amounts when it was made, so a plan
// change is judged here (decision 1, Adam 2026-10-04):
//   * an amount went up      -> the mark no longer applies (unchecked);
//   * same or smaller        -> the mark stands;
//   * the line is gone       -> nothing shows (the mark is kept, and holds
//                               again if the line returns no larger).

export type Mark = "cart" | "have" | "removed";

export interface Amount {
  unit: string;
  quantity: number;
}

export interface GroceryLine {
  key: string;
  name: string;
  category: string;
  amounts: Amount[];
}

// One saved mark (a grocery_marks row). `mark: null` is a cleared mark.
export interface MarkRow {
  weekStart: string;
  itemKey: string;
  mark: Mark | null;
  amounts: Amount[];
  changedAt: string; // ISO time of the tap, from the device
}

export interface GroceryExtra {
  id: string;
  name: string;
  amount: string;
  category: string;
  firstWeek: string;
  lastWeek: string | null; // null = pinned
}

export const extraKey = (id: string) => `extra:${id}`;

const EPSILON = 1e-9;

// Whether any amount is larger now than when the line was marked, or has a
// unit line it didn't have. Marks with no recorded amounts (added items)
// never go up.
export function amountsWentUp(before: Amount[], now: Amount[]): boolean {
  if (before.length === 0) return false;
  return now.some((a) => {
    const was = before.find((b) => b.unit === a.unit);
    return !was || a.quantity > was.quantity + EPSILON;
  });
}

// The mark that applies to a line now, or null.
export function markFor(line: Pick<GroceryLine, "amounts">, row: MarkRow | undefined): Mark | null {
  if (!row || !row.mark) return null;
  return amountsWentUp(row.amounts, line.amounts) ? null : row.mark;
}

// The newer of two versions of the same mark (later tap wins).
export const newer = (a: MarkRow | undefined, b: MarkRow | undefined): MarkRow | undefined => {
  if (!a) return b;
  if (!b) return a;
  return Date.parse(b.changedAt) > Date.parse(a.changedAt) ? b : a;
};

// The week's marks by item key: the saved ones, overlaid with changes still
// waiting to be sent (whichever is newer per line).
export function marksByKey(saved: MarkRow[], pending: MarkRow[], weekStart: string): Map<string, MarkRow> {
  const out = new Map<string, MarkRow>();
  for (const row of [...saved, ...pending]) {
    if (row.weekStart !== weekStart) continue;
    out.set(row.itemKey, newer(out.get(row.itemKey), row)!);
  }
  return out;
}

// The added items on a week's list: one-offs for that week, and pinned
// items from their first week until the week they were unpinned.
export function extrasForWeek(extras: GroceryExtra[], weekStart: string): GroceryExtra[] {
  return extras
    .filter((e) => e.firstWeek <= weekStart && (e.lastWeek === null || e.lastWeek >= weekStart))
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
}

export interface Progress {
  total: number; // lines to deal with: everything not removed
  done: number; // in the cart or already have
  left: number;
  removed: number;
}

// "12 of 31 left": recipe lines and added items together.
export function progress(lines: Pick<GroceryLine, "key" | "amounts">[], extras: GroceryExtra[], marks: Map<string, MarkRow>): Progress {
  const states = [...lines.map((l) => markFor(l, marks.get(l.key))), ...extras.map((e) => markFor({ amounts: [] }, marks.get(extraKey(e.id))))];
  const removed = states.filter((m) => m === "removed").length;
  const done = states.filter((m) => m === "cart" || m === "have").length;
  const total = states.length - removed;
  return { total, done, left: total - done, removed };
}

// ------------------------------------------------------ offline queue ---
// Changes not yet accepted by the server, kept on the phone (decision 2).
// One entry per week and line: a later change replaces an earlier one.

export function enqueue(queue: MarkRow[], row: MarkRow): MarkRow[] {
  return [...queue.filter((q) => !(q.weekStart === row.weekStart && q.itemKey === row.itemKey)), row];
}

// Drops `sent` once the server has it, unless a newer change to the same
// line was queued meanwhile.
export function dequeue(queue: MarkRow[], sent: MarkRow): MarkRow[] {
  return queue.filter((q) => !(q.weekStart === sent.weekStart && q.itemKey === sent.itemKey && q.changedAt === sent.changedAt));
}

const isAmount = (a: unknown): a is Amount => !!a && typeof (a as Amount).unit === "string" && typeof (a as Amount).quantity === "number";
const isRow = (r: unknown): r is MarkRow => {
  const x = r as MarkRow;
  return !!x && typeof x.weekStart === "string" && typeof x.itemKey === "string" && (x.mark === null || x.mark === "cart" || x.mark === "have" || x.mark === "removed") && Array.isArray(x.amounts) && x.amounts.every(isAmount) && typeof x.changedAt === "string";
};

// Reads a stored queue, dropping anything malformed (a hand-edited or
// older-format entry must not break the list).
export function parseQueue(raw: string | null): MarkRow[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isRow) : [];
  } catch {
    return [];
  }
}
